import type { BackOfficeMessageKey } from '@eklevo/shared/i18n/backoffice'
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  WebAuthnError,
  type AuthenticationResponseJSON,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialHint,
  type PublicKeyCredentialRequestOptionsJSON,
  type RegistrationResponseJSON,
} from '@simplewebauthn/browser'
import { errorCode, errorReason } from '../../lib/errors'

/*
 * The browser's side of the staff's passkeys (ADR 0008 §1), with SimpleWebAuthn: the options come
 * from the server, the browser asks the person's device (fingerprint, face or PIN) and the answer
 * goes back to the server, which verifies it. Every failure is explained in words the person can
 * act on (lib: the device or the browser; server: the passkey, the permission, the limits).
 */

export { browserSupportsWebAuthn }

const HINTS: ReadonlySet<string> = new Set(['hybrid', 'security-key', 'client-device'])

/** The creation options as the server sends them (strings where the browser types are unions). */
export type CreationOptions = Omit<PublicKeyCredentialCreationOptionsJSON, 'hints'> & {
  hints: string[]
}

/** Asks the device to create a passkey for these options. */
export function createPasskey(options: CreationOptions): Promise<RegistrationResponseJSON> {
  const hints = options.hints.filter((h): h is PublicKeyCredentialHint => HINTS.has(h))
  return startRegistration({ optionsJSON: { ...options, hints } })
}

/** Asks the device to touch one of the person's passkeys for these options. */
export function touchPasskey(
  options: PublicKeyCredentialRequestOptionsJSON,
): Promise<AuthenticationResponseJSON> {
  return startAuthentication({ optionsJSON: options })
}

/** What went wrong in the browser or on the device, as the person can act on it. */
export type DeviceProblem =
  | 'cancelled'
  | 'already-registered'
  | 'unsupported'
  | 'no-user-verification'
  | 'wrong-address'
  | 'failed'

const BY_CODE: Partial<Record<WebAuthnError['code'], DeviceProblem>> = {
  ERROR_CEREMONY_ABORTED: 'cancelled',
  // NotAllowedError: the person cancelled, or the time ran out (the browsers do not say which).
  ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY: 'cancelled',
  ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED: 'already-registered',
  ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT: 'no-user-verification',
  ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT: 'no-user-verification',
  ERROR_INVALID_DOMAIN: 'wrong-address',
  ERROR_INVALID_RP_ID: 'wrong-address',
  ERROR_AUTHENTICATOR_NO_SUPPORTED_PUBKEYCREDPARAMS_ALG: 'unsupported',
}

const BY_NAME: Record<string, DeviceProblem> = {
  NotAllowedError: 'cancelled',
  AbortError: 'cancelled',
  InvalidStateError: 'already-registered',
  ConstraintError: 'no-user-verification',
  SecurityError: 'wrong-address',
  NotSupportedError: 'unsupported',
}

/** The device's or the browser's problem, or null if the error did not come from WebAuthn. */
export function deviceProblem(err: unknown): DeviceProblem | null {
  if (err instanceof WebAuthnError) return BY_CODE[err.code] ?? 'failed'
  if (err instanceof Error && err.message === 'WebAuthn is not supported in this browser')
    return 'unsupported'
  if (err instanceof DOMException) return BY_NAME[err.name] ?? 'failed'
  return null
}

const DEVICE_MESSAGES: Record<DeviceProblem, BackOfficeMessageKey> = {
  cancelled: 'webauthn.cancelled',
  'already-registered': 'webauthn.alreadyRegistered',
  unsupported: 'webauthn.unsupported',
  'no-user-verification': 'webauthn.noUserVerification',
  'wrong-address': 'webauthn.wrongAddress',
  failed: 'webauthn.failed',
}

/** The server's refusals of a passkey step (apps/api/src/backoffice/trpc/router.ts). */
const SERVER_MESSAGES: Record<string, BackOfficeMessageKey> = {
  'passkey-invalid': 'webauthn.notRecognized',
  'too-many-attempts': 'webauthn.tooManyAttempts',
  'no-challenge': 'webauthn.expired',
  'enrollment-not-allowed': 'webauthn.enrollmentNotAllowed',
  'too-many-passkeys': 'webauthn.tooManyPasskeys',
  'rate-limited': 'common.error.rateLimited',
}

/** The message that explains a failed passkey step, whichever side it failed on. */
export function passkeyErrorMessage(err: unknown): BackOfficeMessageKey {
  const problem = deviceProblem(err)
  if (problem) return DEVICE_MESSAGES[problem]
  const reason = errorReason(err)
  if (reason && reason in SERVER_MESSAGES) return SERVER_MESSAGES[reason]!
  if (errorCode(err) === 'TOO_MANY_REQUESTS') return 'common.error.rateLimited'
  return 'common.error.generic'
}

/*
 * When the person last touched their passkey in this tab, so a sensitive action within the
 * server's 5 minutes (ADR 0008 §4) does not ask for it again. With a margin: the server decides,
 * and a refusal (`reauthenticate`) asks again anyway (components/reauth.tsx).
 */
const REAUTH_MS = 5 * 60_000
const MARGIN_MS = 30_000
let touchedAt: number | null = null

export function rememberTouch(at = Date.now()): void {
  touchedAt = at
}

export function forgetTouch(): void {
  touchedAt = null
}

export function touchedRecently(now = Date.now()): boolean {
  return touchedAt !== null && now - touchedAt < REAUTH_MS - MARGIN_MS
}
